import { safeParseFloat } from '@/lib/utils'
import { Ticker } from '@/types/generated/common/types'

export type BalanceTickerData = { coinPrice: number; todayPnl: number }

/**
 * One flush per window instead of a state write per ticker message; 1 s
 * matches the positions hub's push throttle so spot and derivative rows tick
 * together.
 */
export const BALANCE_TICKER_FLUSH_INTERVAL_MS = 1_000

export function balanceTickerKey(exchange: Ticker['exchange'], symbol: string): string {
  return `${exchange}:${symbol}`
}

/**
 * Leading + trailing throttle: a tick after a quiet window flushes at once,
 * ticks inside the window are held (latest value per key wins) and flushed
 * when it ends.
 */
export function createBalanceTickerBatcher(deps: {
  onFlush: (pending: ReadonlyMap<string, BalanceTickerData>) => void
  intervalMs?: number
  now?: () => number
  setTimeout?: (callback: () => void, delayMs: number) => ReturnType<typeof setTimeout>
  clearTimeout?: (id: ReturnType<typeof setTimeout>) => void
}) {
  const intervalMs = deps.intervalMs ?? BALANCE_TICKER_FLUSH_INTERVAL_MS
  const now = deps.now ?? Date.now
  const schedule = deps.setTimeout ?? ((cb, ms) => setTimeout(cb, ms))
  const cancel = deps.clearTimeout ?? ((id) => clearTimeout(id))

  const pending = new Map<string, BalanceTickerData>()
  let timer: ReturnType<typeof setTimeout> | null = null
  let lastFlushAt = 0

  const flush = () => {
    timer = null
    if (pending.size === 0) return
    lastFlushAt = now()
    // Hand out a snapshot: a React functional updater may run after the next
    // push has already mutated `pending`.
    const batch = new Map(pending)
    pending.clear()
    deps.onFlush(batch)
  }

  return {
    push: (tickers: Ticker[]) => {
      for (const ticker of tickers) {
        if (!ticker.symbol) continue
        pending.set(balanceTickerKey(ticker.exchange, ticker.symbol), {
          coinPrice: safeParseFloat(ticker.lastPrice ?? '0'),
          todayPnl: safeParseFloat(ticker.priceChangePercent24h ?? '0'),
        })
      }
      if (pending.size === 0 || timer) return
      const elapsed = now() - lastFlushAt
      if (elapsed >= intervalMs) flush()
      else timer = schedule(flush, intervalMs - elapsed)
    },
    destroy: () => {
      if (timer) cancel(timer)
      timer = null
      pending.clear()
    },
  }
}
